import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { X, ArrowLeft, ArrowRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  {
    key: "name",
    type: "text",
    label: "First, what's your name?",
    sub: "So we know who we're talking to.",
    placeholder: "Jane Doe",
    required: true,
    autocomplete: "name",
  },
  {
    key: "email",
    type: "email",
    label: "What's the best email to reach you?",
    sub: "We'll send everything here. No spam, ever.",
    placeholder: "jane@company.com",
    required: true,
    autocomplete: "email",
  },
  {
    key: "company",
    type: "text",
    label: "Company or business name?",
    sub: "Optional — leave blank if you're a solo operator.",
    placeholder: "Acme Studio",
    required: false,
  },
  {
    key: "website_or_linkedin",
    type: "text",
    label: "Your website or LinkedIn URL?",
    sub: "Whichever is easier — helps us see your context.",
    placeholder: "acme.com or linkedin.com/in/yourname",
    required: false,
  },
  {
    key: "role",
    type: "choice",
    label: "Which best describes you?",
    sub: null,
    required: true,
    options: ["Founder", "Freelancer or Consultant", "Solopreneur", "Other"],
  },
  {
    key: "what_you_do",
    type: "text",
    label: "In one sentence, what do you do?",
    sub: "Speak like you're at a dinner, not on your website.",
    placeholder: "I help SaaS founders design onboarding that converts.",
    required: true,
  },
  {
    key: "ideal_client",
    type: "text",
    label: "Who is your ideal client or customer?",
    sub: "Optional — the more specific, the better.",
    placeholder: "Early-stage B2B SaaS founders, $500K–$3M ARR",
    required: false,
  },
  {
    key: "linkedin_goal",
    type: "choice",
    label: "What do you want LinkedIn to do for you?",
    sub: null,
    required: false,
    options: [
      "Inbound leads",
      "Authority and visibility",
      "Attract clients",
      "Hiring or partnerships",
      "Other",
    ],
  },
  {
    key: "activity_level",
    type: "choice",
    label: "How active are you on LinkedIn right now?",
    sub: "Be honest — this shapes what we build for you.",
    required: false,
    options: ["I post regularly", "Occasionally", "Rarely or never"],
  },
  {
    key: "edge",
    type: "textarea",
    label:
      "What makes you genuinely good at what you do?",
    sub: "What's a view or edge you have that others in your space don't?",
    placeholder:
      "Most consultants in my space chase tactics. I focus on customer psychology because I spent five years building the product before switching sides...",
    required: true,
  },
  {
    key: "investment_ready",
    type: "choice",
    label:
      "This is a paid engagement at a founding rate. Ready to invest if it's a fit?",
    sub: "One honest answer. No wrong one.",
    required: true,
    options: ["Yes", "Tell me more", "Not right now"],
  },
];

const emptyForm = STEPS.reduce((acc, s) => ({ ...acc, [s.key]: "" }), {});

export default function StarterKitFlow({ open, onClose, onSuccess }) {
  const [idx, setIdx] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const step = STEPS[idx];
  const progress = useMemo(
    () => Math.round(((idx + 1) / STEPS.length) * 100),
    [idx]
  );

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setIdx(0);
      setError("");
      setStatus("idle");
    }
  }, [open]);

  // Autofocus on step change
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      inputRef.current?.focus();
      scrollRef.current?.scrollTo({ top: 0 });
    }, 60);
    return () => clearTimeout(t);
  }, [idx, open]);

  const setValue = (v) =>
    setForm((f) => ({ ...f, [step.key]: v }));

  const validateStep = () => {
    const val = (form[step.key] || "").trim();
    if (step.required && !val) return "This one's required.";
    if (step.type === "email" && val) {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      if (!ok) return "Hmm, that doesn't look like a valid email.";
    }
    return "";
  };

  const advance = async () => {
    const e = validateStep();
    if (e) {
      setError(e);
      return;
    }
    setError("");
    if (idx < STEPS.length - 1) {
      setIdx(idx + 1);
    } else {
      // Submit
      try {
        setStatus("sending");
        await axios.post(`${API}/starter-kit/apply`, form);
        setStatus("success");
        // Short beat then hand off
        setTimeout(() => {
          onSuccess?.();
        }, 900);
      } catch (err) {
        setStatus("idle");
        setError(
          err?.response?.data?.detail ||
            "Something went wrong. Please try again."
        );
      }
    }
  };

  const goBack = () => {
    if (idx > 0) {
      setError("");
      setIdx(idx - 1);
    }
  };

  // Keyboard: Enter to advance (Shift+Enter allowed for textareas)
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && step.type !== "choice") {
      e.preventDefault();
      advance();
    }
  };

  const handleClose = () => {
    // Only confirm if partial progress
    const hasAny = Object.values(form).some((v) => (v || "").trim());
    if (hasAny && status !== "success") {
      const ok = window.confirm(
        "Close this? Your answers won't be saved."
      );
      if (!ok) return;
    }
    setForm(emptyForm);
    setIdx(0);
    setStatus("idle");
    setError("");
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="sk-flow-overlay"
      data-testid="starter-kit-flow-overlay"
      onMouseDown={(e) => {
        // click backdrop to close
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="sk-flow-card" role="dialog" aria-modal="true">
        {/* Top bar */}
        <div className="sk-flow-topbar">
          <button
            type="button"
            onClick={goBack}
            disabled={idx === 0 || status === "success"}
            className="sk-flow-iconbtn"
            data-testid="starter-kit-flow-back"
            aria-label="Previous question"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="sk-flow-progress-wrap">
            <div className="sk-flow-progress-track">
              <div
                className="sk-flow-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="sk-flow-progress-label">
              <span style={{ color: "var(--agero-orange)" }}>
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="opacity-40 mx-1">/</span>
              <span className="opacity-60">
                {String(STEPS.length).padStart(2, "0")}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="sk-flow-iconbtn"
            data-testid="starter-kit-flow-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="sk-flow-body" ref={scrollRef}>
          {status === "success" ? (
            <div className="sk-flow-step" data-testid="starter-kit-flow-success">
              <div
                className="inline-flex items-center justify-center rounded-full mb-6"
                style={{
                  width: 56,
                  height: 56,
                  background: "rgba(255,51,1,0.10)",
                  color: "var(--agero-orange)",
                }}
              >
                <Check size={26} />
              </div>
              <h3
                className="font-display"
                style={{
                  fontSize: "clamp(28px, 3.4vw, 40px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                }}
              >
                Got it. We review every application personally.
              </h3>
              <p
                className="mt-4 text-neutral-600"
                style={{ fontSize: 17 }}
              >
                Opening the booking below so we can talk it through…
              </p>
            </div>
          ) : (
            <div key={idx} className="sk-flow-step sk-flow-step-enter">
              <div className="sk-flow-eyebrow">
                Question {idx + 1} of {STEPS.length}
              </div>
              <h3
                className="font-display sk-flow-q"
                style={{
                  fontSize: "clamp(26px, 3.4vw, 40px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.08,
                }}
              >
                {step.label}
                {step.required && (
                  <span style={{ color: "var(--agero-orange)" }}> *</span>
                )}
              </h3>
              {step.sub && (
                <p
                  className="mt-3 text-neutral-500"
                  style={{ fontSize: 15.5 }}
                >
                  {step.sub}
                </p>
              )}

              <div className="mt-8">
                {step.type === "text" && (
                  <input
                    ref={inputRef}
                    value={form[step.key]}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={step.placeholder}
                    autoComplete={step.autocomplete || "off"}
                    data-testid={`starter-kit-flow-input-${step.key}`}
                    className="sk-flow-input"
                  />
                )}
                {step.type === "email" && (
                  <input
                    ref={inputRef}
                    type="email"
                    value={form[step.key]}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={step.placeholder}
                    autoComplete={step.autocomplete || "email"}
                    data-testid={`starter-kit-flow-input-${step.key}`}
                    className="sk-flow-input"
                  />
                )}
                {step.type === "textarea" && (
                  <textarea
                    ref={inputRef}
                    value={form[step.key]}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={5}
                    placeholder={step.placeholder}
                    data-testid={`starter-kit-flow-input-${step.key}`}
                    className="sk-flow-input sk-flow-textarea"
                  />
                )}
                {step.type === "choice" && (
                  <div className="sk-flow-choices" data-testid={`starter-kit-flow-choices-${step.key}`}>
                    {step.options.map((opt, i) => {
                      const active = form[step.key] === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => {
                            setValue(opt);
                            setError("");
                            // small delay so user sees selection state
                            setTimeout(() => {
                              // advance directly (avoid stale form via closure)
                              if (idx < STEPS.length - 1) {
                                setIdx((i2) => i2 + 1);
                              } else {
                                // last-step choice — trigger submit path
                                (async () => {
                                  try {
                                    setStatus("sending");
                                    await axios.post(
                                      `${API}/starter-kit/apply`,
                                      { ...form, [step.key]: opt }
                                    );
                                    setStatus("success");
                                    setTimeout(() => onSuccess?.(), 900);
                                  } catch (err) {
                                    setStatus("idle");
                                    setError(
                                      err?.response?.data?.detail ||
                                        "Something went wrong. Please try again."
                                    );
                                  }
                                })();
                              }
                            }, 180);
                          }}
                          data-testid={`starter-kit-flow-choice-${step.key}-${i}`}
                          className={`sk-flow-choice ${
                            active ? "is-active" : ""
                          }`}
                        >
                          <span className="sk-flow-choice-key">
                            {String.fromCharCode(65 + i)}
                          </span>
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {error && (
                <div
                  className="mt-4 text-[14px]"
                  style={{ color: "var(--agero-orange)" }}
                  data-testid="starter-kit-flow-error"
                >
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {status !== "success" && (
          <div className="sk-flow-footer">
            <span className="text-[12.5px] text-neutral-500">
              {step.type === "choice" ? (
                <>Pick one to continue.</>
              ) : (
                <>
                  Press <kbd className="sk-kbd">Enter</kbd> to continue
                </>
              )}
            </span>
            {step.type !== "choice" && (
              <button
                type="button"
                onClick={advance}
                disabled={status === "sending"}
                data-testid="starter-kit-flow-next"
                className="sk-flow-nextbtn"
              >
                {status === "sending"
                  ? "Sending..."
                  : idx === STEPS.length - 1
                  ? "Send my application"
                  : "Next"}
                {status !== "sending" && <ArrowRight size={16} />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
